import type { Entity } from '../../types/infrastructure'
import type { NewsCategory, NewsEvidenceMatch, NewsItem } from '../../types/news'

const categoryOverviewEntities: Record<NewsCategory, string[]> = {
  'AI / COMPUTE': ['overview-demand', 'overview-compute'],
  'DATA CENTERS': ['overview-datacenters'],
  GRID: ['overview-grid', 'overview-interconnect'],
  POWER: ['overview-power'],
  'NUCLEAR / FUEL': ['overview-nuclear', 'overview-fuel'],
  CAPITAL: ['overview-capital'],
  GOVERNMENT: ['overview-policy'],
  COMMUNITIES: ['overview-community'],
}

const genericAliases = new Set(['capital', 'community', 'equipment', 'government', 'interconnect', 'power', 'water', 'workforce'])
const corporateSuffixes = /\s+(?:america|corporation|company|energy|holdings|platforms|technologies)$/i
const normalize = (value: string) => ` ${value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `

function aliasesFor(entity: Entity): string[] {
  const names = [entity.name, entity.shortName, ...entity.name.split(/\s*\/\s*/)]
  if (entity.entityType === 'company') names.push(entity.name.replace(corporateSuffixes, ''))
  return Array.from(new Set(names.map((name) => normalize(name).trim()).filter((name) => name.length >= 4 && !genericAliases.has(name))))
}

export function matchNewsEvidence(items: NewsItem[], entities: Entity[]): Map<string, NewsEvidenceMatch[]> {
  const matches = new Map<string, NewsEvidenceMatch[]>()
  const entityIds = new Set(entities.map((entity) => entity.id))
  const factualEntities = entities.filter((entity) => !entity.id.startsWith('overview-'))
  const aliases = new Map<string, Entity[]>()

  factualEntities.forEach((entity) => aliasesFor(entity).forEach((alias) => aliases.set(alias, [...(aliases.get(alias) ?? []), entity])))

  const add = (entityId: string, item: NewsItem, reason: NewsEvidenceMatch['reason']) => {
    if (!entityIds.has(entityId)) return
    const current = matches.get(entityId) ?? []
    if (current.some((match) => match.item.id === item.id)) return
    current.push({ id: `${entityId}:${item.id}`, item, reason, confidence: reason === 'entity-name' ? 'high' : 'contextual', reviewState: 'discovered' })
    matches.set(entityId, current)
  }

  for (const item of items) {
    item.categories.forEach((category) => {
      if (!(category in categoryOverviewEntities)) return
      categoryOverviewEntities[category as NewsCategory].forEach((entityId) => add(entityId, item, 'system-category'))
    })
    const haystack = normalize(`${item.title} ${item.summary ?? ''} ${item.publisher}`)
    aliases.forEach((owners, alias) => {
      if (owners.length === 1 && haystack.includes(` ${alias} `)) add(owners[0].id, item, 'entity-name')
    })
  }

  matches.forEach((entityMatches) => entityMatches.sort((a, b) => new Date(b.item.publishedAt).getTime() - new Date(a.item.publishedAt).getTime()))
  return matches
}
